import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  computed,
  inject,
} from "@angular/core";
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from "@angular/core/rxjs-interop";
import {
  EMPTY,
  ReplaySubject,
  auditTime,
  catchError,
  debounceTime,
  filter,
  merge,
  map,
  of,
  switchMap,
} from "rxjs";
import {AppNewsPublic} from "src/app/interfaces/app-news.interface";
import {AppNewsService} from "src/app/services/app-news.service";
import {AppUpdateService} from "src/app/services/appUpdate.service";
import {ModalService} from "../../services/modal.service";
import {ModalComponent} from "../modal/modal.component";
import {NewsContentComponent} from "../news-content/news-content.component";

@Component({
  selector: "app-news-modal",
  templateUrl: "./news-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ModalComponent, NewsContentComponent],
  // Keep an open announcement available after the native update dialog is dismissed.
  host: {"[hidden]": "updates.mobileUpdate() !== null"},
})
export class NewsModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input({required: true}) userId = "";
  @Input() enabled = false;
  news: AppNewsPublic | null = null;
  private readonly api = inject(AppNewsService);
  protected readonly updates = inject(AppUpdateService);
  private readonly canShowNews = computed(
    () => this.updates.mobileUpdateChecked() && !this.updates.mobileUpdate()
  );
  private readonly updateAvailability = toObservable(this.canShowNews);
  private readonly modalService = inject(ModalService);
  protected readonly isNewsOpen = toSignal(
    this.modalService.modals$.pipe(map((modals) => !!modals["news"])),
    {initialValue: false}
  );
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly refresh = new ReplaySubject<void>(1);
  private readonly seen = new Map<string, number>();

  ngOnInit(): void {
    merge(
      this.refresh,
      this.updates.appVersion$,
      this.updateAvailability,
      this.modalService.modalOpened.pipe(
        filter((event) => !!event && !event.opened && event.modalId !== "news"),
        // Match the shared modal's 300ms close transition before opening news.
        debounceTime(300)
      )
    )
      .pipe(
        auditTime(0),
        switchMap(() => {
          const userId = this.userId;
          if (
            !this.enabled ||
            !this.canShowNews() ||
            !userId ||
            this.modalService.hasOpenModal()
          )
            return of({news: null});
          return this.api.pending().pipe(
            catchError(() => of({news: null})),
            filter(() => userId === this.userId)
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({news}) => this.show(news));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.enabled || changes["userId"]) this.closeNewsModal();
    this.refresh.next();
  }

  closeNewsModal(): void {
    if (this.modalService.isModalOpen("news"))
      this.modalService.closeModal("news");
  }

  private show(news: AppNewsPublic | null): void {
    if (
      !news ||
      !this.enabled ||
      !this.canShowNews() ||
      !this.userId ||
      document.hidden ||
      this.modalService.hasOpenModal()
    )
      return;
    const seenRevision = this.seenRevision();
    if (seenRevision < news.revision) {
      this.news = news;
      this.cdr.detectChanges();
      this.modalService.openModal("news");
      this.seen.set(this.userId, news.revision);
      try {
        localStorage.setItem(this.storageKey, String(news.revision));
      } catch {
        // Account-specific memory remains available when storage is restricted.
      }
    }
    // Retry a previous failed acknowledgment without showing the same content again.
    this.api
      .markSeen(news.revision)
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private get storageKey(): string {
    return `app_news_seen_${this.userId}`;
  }

  private seenRevision(): number {
    const memory = this.seen.get(this.userId) ?? 0;
    try {
      const stored = Number(localStorage.getItem(this.storageKey));
      return Number.isSafeInteger(stored) ? Math.max(memory, stored) : memory;
    } catch {
      return memory;
    }
  }

  ngOnDestroy(): void {
    this.closeNewsModal();
  }
}
